import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { AppShell } from "@/components/AppShell";
import { BadgeCheck, Store, ShieldCheck, FileText, AlertTriangle } from "lucide-react";
import {
  submitStoreVerification,
  getMyStoreVerification,
  createVerificationUploadUrl,
  type StoreVerificationApplication,
  type VerificationTier,
} from "@/lib/store-verification.functions";
import { supabase } from "@/integrations/supabase/client";

export const Route = createFileRoute("/_authenticated/seller-verification")({
  head: () => ({
    meta: [
      { title: "Seller Verification — Want To Buy" },
      { name: "description", content: "Apply for Verified Business or Verified Store badge." },
    ],
  }),
  component: SellerVerificationPage,
});

function SellerVerificationPage() {
  const fnSubmit = useServerFn(submitStoreVerification);
  const fnGet = useServerFn(getMyStoreVerification);
  const fnSignUpload = useServerFn(createVerificationUploadUrl);

  const [loading, setLoading] = useState(true);
  const [app, setApp] = useState<StoreVerificationApplication | null>(null);

  const [tier, setTier] = useState<VerificationTier>("verified_business");
  const [name, setName] = useState("");
  const [reg, setReg] = useState("");
  const [email, setEmail] = useState("");
  const [website, setWebsite] = useState("");
  const [city, setCity] = useState("");
  const [province, setProvince] = useState("");
  const [idDocPath, setIdDocPath] = useState<string | null>(null);
  const [poaPath, setPoaPath] = useState<string | null>(null);
  const [photoPaths, setPhotoPaths] = useState<string[]>([]);
  const [declaration, setDeclaration] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { application } = await fnGet();
        setApp(application);
      } catch (e) {
        toast.error((e as Error).message);
      } finally {
        setLoading(false);
      }
    })();
  }, [fnGet]);

  async function uploadFile(file: File, kind: "id" | "poa" | "photo"): Promise<string | null> {
    const ext = file.name.split(".").pop()?.toLowerCase() ?? "bin";
    const { data: userData } = await supabase.auth.getUser();
    const uid = userData.user?.id;
    if (!uid) {
      toast.error("Not signed in");
      return null;
    }
    const path = `${uid}/${kind}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}.${ext}`;
    try {
      const { token } = await fnSignUpload({ data: { path } });
      const { error } = await supabase.storage
        .from("store-verification-docs")
        .uploadToSignedUrl(path, token, file);
      if (error) throw error;
      return path;
    } catch (e) {
      toast.error(`Upload failed: ${(e as Error).message}`);
      return null;
    }
  }

  async function handleIdUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0];
    if (!f) return;
    const p = await uploadFile(f, "id");
    if (p) {
      setIdDocPath(p);
      toast.success("ID uploaded");
    }
  }
  async function handlePoaUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0];
    if (!f) return;
    const p = await uploadFile(f, "poa");
    if (p) {
      setPoaPath(p);
      toast.success("Proof of address uploaded");
    }
  }
  async function handlePhotoUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const files = Array.from(e.target.files ?? []);
    for (const f of files) {
      const p = await uploadFile(f, "photo");
      if (p) setPhotoPaths((cur) => [...cur, p]);
    }
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!declaration) {
      toast.error("You must accept the declaration");
      return;
    }
    setBusy(true);
    try {
      await fnSubmit({
        data: {
          requested_tier: tier,
          business_name: name,
          business_registration_number: reg,
          business_email: email,
          business_website: website,
          store_city: city || null,
          store_province: province || null,
          id_document_path: idDocPath,
          proof_of_address_path: poaPath,
          store_photo_paths: photoPaths,
          declaration_accepted: true,
        },
      });
      toast.success("Application submitted — admins will review shortly");
      const { application } = await fnGet();
      setApp(application);
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  if (loading) {
    return (
      <AppShell>
        <div className="mx-auto max-w-3xl py-20 text-center text-sm text-muted-foreground">Loading…</div>
      </AppShell>
    );
  }

  // If app exists and not actionable, show status
  const isActionable = !app || app.status === "needs_info" || app.status === "rejected";

  return (
    <AppShell>
      <div className="mx-auto max-w-3xl space-y-6">
        <header>
          <div className="text-xs font-semibold uppercase tracking-wider text-primary">Seller verification</div>
          <h1 className="mt-1 font-display text-3xl font-bold">Verified Store program</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            Apply once. Get a visible trust badge across the platform plus a listing in the public Verified Stores directory.
          </p>
        </header>

        {app && (
          <section
            className={`panel p-5 ${
              app.status === "approved"
                ? "border-success/40 bg-success/5"
                : app.status === "rejected"
                ? "border-destructive/40 bg-destructive/5"
                : app.status === "needs_info"
                ? "border-warning/40 bg-warning/5"
                : "border-primary/40 bg-primary/5"
            }`}
          >
            <h2 className="font-display text-base font-bold">
              Application status: <span className="capitalize">{app.status.replace(/_/g, " ")}</span>
            </h2>
            <p className="mt-1 text-xs text-muted-foreground">
              Submitted {new Date(app.created_at).toLocaleString()} · Tier requested:{" "}
              {app.requested_tier === "verified_store" ? "Verified Store" : "Verified Business"}
              {app.granted_tier && app.granted_tier !== app.requested_tier && (
                <> · Granted: {app.granted_tier === "verified_store" ? "Verified Store" : "Verified Business"}</>
              )}
            </p>
            {app.admin_message && (
              <p className="mt-3 whitespace-pre-wrap rounded-md border border-border bg-background/40 p-3 text-sm">
                <strong>Admin message:</strong> {app.admin_message}
              </p>
            )}
          </section>
        )}

        <section className="grid gap-4 sm:grid-cols-2">
          <TierCard
            icon={<BadgeCheck className="h-5 w-5 text-blue-500" />}
            title="Verified Business"
            subtitle="Tier 1"
            benefits={[
              "Blue Verified Business badge",
              "Higher buyer trust on offers",
              "Eligible for Trust & Safety priority response",
            ]}
            selected={tier === "verified_business"}
            onSelect={() => setTier("verified_business")}
            disabled={!isActionable}
          />
          <TierCard
            icon={<Store className="h-5 w-5 text-indigo-500" />}
            title="Verified Store"
            subtitle="Tier 2 — includes Tier 1"
            benefits={[
              "Indigo Verified Store badge with city + province",
              "Listed in public Verified Stores directory",
              "Marketing priority — featured to serious buyers",
            ]}
            selected={tier === "verified_store"}
            onSelect={() => setTier("verified_store")}
            disabled={!isActionable}
          />
        </section>

        <section className="panel p-5 text-xs text-muted-foreground">
          <div className="flex items-start gap-3">
            <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-success" />
            <p>
              Documents are uploaded to a private, admin-only bucket. They are used solely for identity
              verification, never shared, and automatically deleted 90 days after your application is approved or
              rejected — in compliance with Quebec Law 25 data minimisation. See our{" "}
              <a className="underline" href="/policies?tab=platform">Privacy Policy</a> for details.
            </p>
          </div>
        </section>

        <section className="panel border-amber-400/40 bg-amber-400/5 p-5 text-xs">
          <div className="flex items-start gap-3">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-500" />
            <div>
              <p className="font-semibold text-foreground">Application fee — $19.99 CAD (one-time, non-refundable)</p>
              <p className="mt-1 text-muted-foreground">
                Covers manual review costs and is non-refundable regardless of outcome. <strong>Coming soon</strong> — during
                the beta period applications are processed <strong>free of charge</strong> as an early-adopter benefit.
              </p>
            </div>
          </div>
        </section>

        {isActionable && (
          <form onSubmit={submit} className="panel space-y-5 p-5">
            <h2 className="font-display text-lg font-bold">Application form</h2>

            <Field label="Business name">
              <input className="input-base" value={name} onChange={(e) => setName(e.target.value)} required />
            </Field>
            <Field label="Business registration number">
              <input className="input-base" value={reg} onChange={(e) => setReg(e.target.value)} required />
            </Field>
            <Field label="Business email address">
              <input type="email" className="input-base" value={email} onChange={(e) => setEmail(e.target.value)} required />
            </Field>
            <Field label="Store website or social media link">
              <input className="input-base" value={website} onChange={(e) => setWebsite(e.target.value)} placeholder="https://" required />
            </Field>

            {tier === "verified_store" && (
              <>
                <div className="grid gap-3 sm:grid-cols-2">
                  <Field label="Store city">
                    <input className="input-base" value={city} onChange={(e) => setCity(e.target.value)} required />
                  </Field>
                  <Field label="Province / state">
                    <input className="input-base" value={province} onChange={(e) => setProvince(e.target.value)} required />
                  </Field>
                </div>

                <Field label="Government-issued ID (required)">
                  <input type="file" accept="image/*,application/pdf" onChange={handleIdUpload} className="text-xs" />
                  {idDocPath && <p className="mt-1 text-[11px] text-success">Uploaded ✓</p>}
                </Field>
                <Field label="Proof of address (required)">
                  <input type="file" accept="image/*,application/pdf" onChange={handlePoaUpload} className="text-xs" />
                  {poaPath && <p className="mt-1 text-[11px] text-success">Uploaded ✓</p>}
                </Field>
                <Field label="Store photos (optional, up to 10)">
                  <input type="file" accept="image/*" multiple onChange={handlePhotoUpload} className="text-xs" />
                  {photoPaths.length > 0 && (
                    <p className="mt-1 text-[11px] text-success">{photoPaths.length} photo(s) uploaded ✓</p>
                  )}
                </Field>
              </>
            )}

            <label className="flex gap-3 rounded-md border border-border bg-background/40 p-3 text-xs">
              <input
                type="checkbox"
                checked={declaration}
                onChange={(e) => setDeclaration(e.target.checked)}
                className="mt-1 h-4 w-4"
              />
              <span>
                <strong>Verified Seller Declaration:</strong> I confirm that the information provided is accurate and
                truthful, and that I am authorised to represent the business named above. I understand that
                falsifying information will result in account termination.
              </span>
            </label>

            <div className="flex justify-end gap-2">
              <button type="submit" disabled={busy || !declaration} className="btn-primary px-4 py-2 text-sm disabled:opacity-60">
                {busy ? "Submitting…" : "Submit application"}
              </button>
            </div>
          </form>
        )}
      </div>

      <style>{`.input-base { width:100%; height:2.5rem; border-radius:0.375rem; border:1px solid var(--color-border); background: var(--color-background); padding: 0 0.75rem; font-size: 0.875rem; color: var(--color-foreground); }
      .input-base:focus { outline: none; border-color: var(--color-primary); box-shadow: 0 0 0 2px color-mix(in oklab, var(--color-primary) 30%, transparent); }`}</style>
    </AppShell>
  );
}

function Field({ label, children }: { label: React.ReactNode; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">{label}</span>
      {children}
    </label>
  );
}

function TierCard({
  icon,
  title,
  subtitle,
  benefits,
  selected,
  onSelect,
  disabled,
}: {
  icon: React.ReactNode;
  title: string;
  subtitle: string;
  benefits: string[];
  selected: boolean;
  onSelect: () => void;
  disabled: boolean;
}) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onSelect}
      className={`panel p-5 text-left transition disabled:opacity-60 ${
        selected ? "border-primary ring-2 ring-primary/40" : "hover:border-primary/40"
      }`}
    >
      <div className="flex items-center gap-2">
        {icon}
        <div>
          <div className="font-display text-base font-bold">{title}</div>
          <div className="text-[11px] uppercase tracking-wider text-muted-foreground">{subtitle}</div>
        </div>
      </div>
      <ul className="mt-3 space-y-1.5 text-xs">
        {benefits.map((b) => (
          <li key={b} className="flex gap-2">
            <FileText className="mt-0.5 h-3 w-3 shrink-0 text-primary" /> <span>{b}</span>
          </li>
        ))}
      </ul>
    </button>
  );
}
